import { useState, useEffect, useRef } from 'react';
import { Accessibility, X, RotateCcw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface AccessibilitySettings {
  largeText: boolean;
  highContrast: boolean;
  grayscale: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
}

const defaultSettings: AccessibilitySettings = {
  largeText: false,
  highContrast: false,
  grayscale: false,
  highlightLinks: false,
  readableFont: false,
};

const STORAGE_KEY = 'accessibility-settings';

export function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load settings from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as AccessibilitySettings;
        setSettings(parsed);
      } catch (e) {
        console.error('Failed to parse accessibility settings:', e);
      }
    }
  }, []);

  // Apply CSS classes to document.documentElement whenever settings change
  useEffect(() => {
    const html = document.documentElement;

    // Large Text
    html.classList.toggle('a11y-large-text', settings.largeText);

    // High Contrast
    html.classList.toggle('a11y-high-contrast', settings.highContrast);

    // Grayscale
    html.classList.toggle('a11y-grayscale', settings.grayscale);

    // Highlight Links
    html.classList.toggle('a11y-highlight-links', settings.highlightLinks);

    // Readable Font
    html.classList.toggle('a11y-readable-font', settings.readableFont);

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (key: keyof AccessibilitySettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleReset = () => {
    setSettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  const hasActiveSettings = Object.values(settings).some(Boolean);

  return (
    <div ref={panelRef} className="fixed bottom-6 right-6 z-40" dir="rtl">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 right-0 w-72 bg-background border border-border shadow-xl overflow-hidden"
            style={{ borderRadius: '12px' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
              <h3 className="font-semibold text-foreground">הגדרות נגישות</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Options */}
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="large-text" className="text-sm text-foreground">
                  הגדלת טקסט
                </label>
                <Switch
                  id="large-text"
                  checked={settings.largeText}
                  onCheckedChange={() => handleToggle('largeText')}
                />
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="high-contrast" className="text-sm text-foreground">
                  ניגודיות גבוהה
                </label>
                <Switch
                  id="high-contrast"
                  checked={settings.highContrast}
                  onCheckedChange={() => handleToggle('highContrast')}
                />
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="grayscale" className="text-sm text-foreground">
                  גווני אפור
                </label>
                <Switch
                  id="grayscale"
                  checked={settings.grayscale}
                  onCheckedChange={() => handleToggle('grayscale')}
                />
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="highlight-links" className="text-sm text-foreground">
                  הדגשת קישורים
                </label>
                <Switch
                  id="highlight-links"
                  checked={settings.highlightLinks}
                  onCheckedChange={() => handleToggle('highlightLinks')}
                />
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="readable-font" className="text-sm text-foreground">
                  גופן קריא
                </label>
                <Switch
                  id="readable-font"
                  checked={settings.readableFont}
                  onCheckedChange={() => handleToggle('readableFont')}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border space-y-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleReset}
                disabled={!hasActiveSettings}
              >
                <RotateCcw className="h-4 w-4 ml-2" />
                איפוס הגדרות
              </Button>

              <Link
                to="/accessibility"
                className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsOpen(false)}
              >
                הצהרת נגישות
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="h-14 w-14 aspect-square shadow-lg bg-primary hover:bg-primary/90"
        style={{ borderRadius: '50%' }}
        size="icon"
        aria-label="הגדרות נגישות"
      >
        <Accessibility className="h-6 w-6" />
      </Button>
    </div>
  );
}
